import React, { useState, useEffect, useMemo } from 'react';
import lumiObserver from '../lumiObserver';

// ── Haber process equilibrium: N₂ + 3H₂ ⇌ 2NH₃, ΔH = -92 kJ/mol (exothermic)
// Qualitative Le Chatelier model — shifts direction based on stress type.
// Forward-shift (more NH₃) is favoured by:
//   • lower T (exothermic → Le Chatelier opposes T increase by going endothermic, i.e. reverse)
//   • higher P (4 mol reactant → 2 mol product, fewer moles on product side)
//   • adding N₂ or H₂
//   • removing NH₃
// ────────────────────────────────────────────────────────────────────────

const T = {
  bg: '#0D1117', surface: '#161B22', border: '#30363D',
  text: '#E6EDF3', textMid: '#8B949E',
  accent: '#0B8A7E', correct: '#10B981', wrong: '#EF4444', hint: '#F59E0B',
};

// Challenge definitions — expected moves toward success.
const CHALLENGES = [
  {
    id: 1, name: 'Maximise ammonia yield',
    description: 'Move the equilibrium as far right as you can. Target: [NH₃] ≥ 0.60 mol/L.',
    target: { species: 'NH3', op: '>=', value: 0.60 },
    // Moves that help (forward shift). Anything not in this list is either
    // neutral or counterproductive.
    helpfulMoves: ['lower_temperature', 'raise_pressure', 'add_N2', 'add_H2', 'remove_NH3'],
  },
];

export default function LeChatelier({ interactiveId = 'le_chatelier_p1' }) {
  // Parameters a student can adjust
  const [temperature, setTemperature] = useState(500); // K
  const [pressure, setPressure] = useState(100);       // atm
  const [N2, setN2] = useState(0.20);                  // mol/L starting stock
  const [H2, setH2] = useState(0.60);
  const [NH3, setNH3] = useState(0.05);
  const [challengeIdx, setChallengeIdx] = useState(0);
  const [challengeStatus, setChallengeStatus] = useState('idle'); // idle | success | fail

  // Equilibrium computation — qualitative Le Chatelier scoring.
  // We don't solve Kp/Kc; instead we compute a "forward shift score" from the
  // stress variables and apply it to the stock concentrations, then report
  // equilibrium concentrations back. This lets the student feel the *direction*
  // of shift, which is what Le Chatelier is about.
  const equilibrium = useMemo(() => {
    // Reference state: T=500 K, P=100 atm, equal stock.
    const tScore = (500 - temperature) / 300;          // cold favours forward
    const pScore = (pressure - 100) / 200;             // high P favours forward
    const nScore = (N2 - 0.20) * 2;                    // adding N₂ favours forward
    const hScore = (H2 - 0.60) * 1.2;
    const removeNH3 = Math.max(0, 0.05 - NH3) * 4;     // removing NH₃ favours forward
    const shift = Math.max(-1, Math.min(1, tScore + pScore + nScore + hScore + removeNH3 - Math.max(0, NH3 - 0.05) * 2));

    // Final concentrations after "reaction": reactants decrease, product increases.
    const eqNH3 = Math.max(0, Math.min(1.2, NH3 + shift * 0.55));
    const eqN2  = Math.max(0, N2 - shift * 0.12);
    const eqH2  = Math.max(0, H2 - shift * 0.36);
    return { NH3: eqNH3, N2: eqN2, H2: eqH2, shift };
  }, [temperature, pressure, N2, H2, NH3]);

  // Emit slider_moved events to the observer. Debounced-ish by comparing to
  // previous value via ref equivalent through useEffect.
  const emit = (type, payload) => lumiObserver.emit({ interactiveId, type, payload });

  useEffect(() => { emit('equilibrium_reached', { ...equilibrium }); /* eslint-disable-next-line */ }, [equilibrium.NH3.toFixed(3)]);

  const startChallenge = () => {
    const c = CHALLENGES[challengeIdx];
    setChallengeStatus('idle');
    emit('challenge_started', { challenge: c.id, name: c.name });
  };
  const checkChallenge = () => {
    const c = CHALLENGES[challengeIdx];
    const v = equilibrium[c.target.species];
    const passed = c.target.op === '>=' ? v >= c.target.value : v <= c.target.value;
    if (passed) {
      setChallengeStatus('success');
      emit('challenge_completed', { challenge: c.id, finalNH3: v, expectedActions: 4 });
    } else {
      setChallengeStatus('fail');
      emit('challenge_failed', { challenge: c.id, finalNH3: v, target: c.target.value });
    }
  };

  // Report any counterproductive move as a misconception signal if the
  // student specifically *removes* N₂ or H₂ or *adds* NH₃ while aiming for
  // "maximise yield".
  const onSlider = (name, prev, next) => {
    emit('slider_moved', { param: name, from: prev, to: next });
    const c = CHALLENGES[challengeIdx];
    if (!c) return;
    const rule = {
      N2:  (p, n) => n < p ? 'remove_N2' : 'add_N2',
      H2:  (p, n) => n < p ? 'remove_H2' : 'add_H2',
      NH3: (p, n) => n > p ? 'add_NH3' : 'remove_NH3',
      temperature: (p, n) => n < p ? 'lower_temperature' : 'raise_temperature',
      pressure:    (p, n) => n > p ? 'raise_pressure' : 'lower_pressure',
    }[name];
    if (!rule) return;
    const move = rule(prev, next);
    if (!c.helpfulMoves.includes(move) && move !== 'raise_temperature' && move !== 'lower_pressure') {
      // Dedicated misconception flag — interpreter picks up via the generic hook
      emit('misconception_flag', { kind: 'counterproductive_move', move, challenge: c.id });
    }
  };

  const Slider = ({ label, value, setValue, min, max, step, unit, onEmit }) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, color: T.textMid }}>
        <span>{label}</span><span style={{ color: T.accent, fontWeight: 800 }}>{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => { const n = Number(e.target.value); onEmit?.(value, n); setValue(n); }}
        style={{ width: '100%', accentColor: T.accent }} aria-label={label} />
    </div>
  );

  const bar = (species, max, color) => {
    const v = equilibrium[species];
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: T.textMid, width: 40 }}>{species.replace('2', '₂').replace('3', '₃')}</span>
        <div style={{ flex: 1, height: 18, background: T.bg, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(100, (v / max) * 100)}%`, height: '100%', background: color, transition: 'width .35s' }} />
        </div>
        <span style={{ fontSize: 11, color: T.text, width: 60, textAlign: 'right' }}>{v.toFixed(3)} M</span>
      </div>
    );
  };

  const c = CHALLENGES[challengeIdx];

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div style={{ fontSize: 11, color: T.textMid, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 700 }}>Chemistry · HSC Module 5 · Equilibrium</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>Le Chatelier — Haber Process</div>
      <div style={{ fontSize: 13, color: T.textMid, marginTop: 4 }}>
        N₂(g) + 3H₂(g) ⇌ 2NH₃(g), ΔH = −92 kJ/mol
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 14 }}>
        <div>
          <div style={{ fontSize: 12, color: T.textMid, fontWeight: 700, marginBottom: 6 }}>Conditions</div>
          <Slider label="Temperature" value={temperature} setValue={setTemperature} min={200} max={800} step={10} unit=" K" onEmit={(p, n) => onSlider('temperature', p, n)} />
          <Slider label="Pressure"    value={pressure}    setValue={setPressure}    min={10}  max={500} step={5}  unit=" atm" onEmit={(p, n) => onSlider('pressure', p, n)} />
          <Slider label="[N₂] added"  value={N2}          setValue={setN2}          min={0}   max={1}   step={0.02} unit=" M"   onEmit={(p, n) => onSlider('N2', p, n)} />
          <Slider label="[H₂] added"  value={H2}          setValue={setH2}          min={0}   max={1.5} step={0.05} unit=" M"   onEmit={(p, n) => onSlider('H2', p, n)} />
          <Slider label="[NH₃] initial" value={NH3}       setValue={setNH3}         min={0}   max={0.5} step={0.01} unit=" M"   onEmit={(p, n) => onSlider('NH3', p, n)} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textMid, fontWeight: 700, marginBottom: 6 }}>At equilibrium</div>
          {bar('N2',  1,    '#64748B')}
          {bar('H2',  1.5,  '#64748B')}
          {bar('NH3', 1.2,  T.accent)}
          <div style={{ fontSize: 11, color: T.textMid, marginTop: 8 }}>
            Shift: {equilibrium.shift > 0.1 ? '→ forward' : equilibrium.shift < -0.1 ? '← reverse' : 'neutral'}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14, padding: 12, background: T.bg, borderRadius: 10, border: `1px solid ${T.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 12, color: T.hint, fontWeight: 800 }}>Challenge {c.id}</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{c.name}</div>
            <div style={{ fontSize: 12, color: T.textMid, marginTop: 2 }}>{c.description}</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={startChallenge} style={btnSecondary()}>Start</button>
            <button onClick={checkChallenge} style={btnPrimary()}>Check</button>
          </div>
        </div>
        {challengeStatus === 'success' && (
          <div style={feedback(T.correct)}>✓ You drove [NH₃] to {equilibrium.NH3.toFixed(3)} M — target was {c.target.value}.</div>
        )}
        {challengeStatus === 'fail' && (
          <div style={feedback(T.wrong)}>✗ [NH₃] is only {equilibrium.NH3.toFixed(3)} M. Target was {c.target.value}.</div>
        )}
      </div>
    </div>
  );
}

const btnPrimary = () => ({ padding: '8px 14px', background: T.accent, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 });
const btnSecondary = () => ({ padding: '8px 14px', background: T.surface, color: T.textMid, border: `1px solid ${T.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 13 });
const feedback = (color) => ({ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: color + '22', border: `1px solid ${color}`, color, fontSize: 13, fontWeight: 600 });
